import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import {
  ShieldCheck, ShieldX, ShieldAlert, Loader2, ArrowLeft, Download,
  FileText, User, AlertTriangle, CheckCircle2,
  Send, X, UserPlus, Mail
} from "lucide-react";
import BonsaiMate from "../components/BonsaiMate";
import NextStepBanner from "../components/NextStepBanner";
import ConflictSendForApproval from "../components/ConflictSendForApproval";
import { clientDisplayName } from "../utils/clientDisplayName";

export default function ConflictCheckDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cc, setCc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showOBModal, setShowOBModal] = useState(false);
  const [obForm, setObForm] = useState({
    client_email: "",
    fee_earner: "",
    fee_earner_email: "",
    matter_description: "",
    joint_clients: [],
  });
  const [obSending, setObSending] = useState(false);
  const [obResult, setObResult] = useState(null);
  const [obError, setObError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/conflict-checks/${id}`);
        const data = res.data;
        setCc(data);

        setObForm((f) => ({
          ...f,
          fee_earner: data.fee_earner || "",
          fee_earner_email: data.fee_earner_email || "",
          matter_description: data.matter_type || data.search_query || "",
        }));
      } catch (e) {
        console.error("Failed to load conflict check:", e?.response?.data || e);
        setError(errorMessage(e, "Conflict check not found"));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const canSendOB =
    cc && ["clear", "clear_with_conditions", "na", "not_ready"].includes(cc.conflict_status);

  const handleSendOB = async () => {
    const cleanedClientEmail = obForm.client_email.trim();
    const cleanedFeeEarner = obForm.fee_earner.trim();
    const cleanedFeeEarnerEmail = obForm.fee_earner_email.trim();
    const cleanedMatterDescription = obForm.matter_description.trim();

    const cleanedJointClients = obForm.joint_clients
      .map((j) => ({
        name: (j.name || "").trim(),
        email: (j.email || "").trim(),
      }))
      .filter((j) => j.name && j.email);

    if (!cc?.client_name?.trim()) {
      setObError("This conflict check does not have a client name.");
      return;
    }

    if (!cleanedClientEmail) {
      setObError("Please enter the client email.");
      return;
    }

    if (!cleanedFeeEarner) {
      setObError("Please enter the fee earner.");
      return;
    }

    setObSending(true);
    setObError(null);

    const payload = {
      conflict_check_id: id,
      client_name: cc.client_name.trim(),
      client_email: cleanedClientEmail,
      fee_earner: cleanedFeeEarner,
      fee_earner_email: cleanedFeeEarnerEmail,
      matter_description: cleanedMatterDescription,
      joint_clients: cleanedJointClients,
    };

    try {
      console.log("Sending onboarding from conflict check:", payload);

      const res = await axios.post(
        `${API}/onboarding/send-from-conflict-check`,
        payload
      );

      console.log("Onboarding response:", res.data);
      setObResult(res.data);
    } catch (e) {
      console.error("Failed to send onboarding:", e?.response?.data || e);

      const backendMessage =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        (Array.isArray(e?.response?.data?.errors)
          ? e.response.data.errors.join(", ")
          : null);

      setObError(backendMessage || "Failed to send onboarding. Please try again.");
    } finally {
      setObSending(false);
    }
  };

  const addJointClient = () => {
    setObForm((f) => ({
      ...f,
      joint_clients: [...f.joint_clients, { name: "", email: "" }],
    }));
  };

  const updateJointClient = (i, field, val) => {
    setObForm((f) => {
      const jc = [...f.joint_clients];
      jc[i] = { ...jc[i], [field]: val };
      return { ...f, joint_clients: jc };
    });
  };

  const removeJointClient = (i) => {
    setObForm((f) => ({
      ...f,
      joint_clients: f.joint_clients.filter((_, idx) => idx !== i),
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal" />
      </div>
    );
  }

  if (error || !cc) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-4">
        <div className="bg-navy border border-navy-border rounded-lg p-8 max-w-md w-full text-center">
          <ShieldX className="w-12 h-12 text-status-red mx-auto mb-4" />
          <h2 className="text-xl font-medium text-status-red mb-2">Conflict Check Not Found</h2>
          <p className="text-txt-muted text-sm mb-4">{error}</p>
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="border-navy-border text-txt-muted"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = {
    clear: {
      icon: ShieldCheck,
      color: "text-teal",
      bg: "bg-teal-dim/30",
      border: "border-teal/50",
      label: "Cleared",
    },
    clear_with_conditions: {
      icon: ShieldAlert,
      color: "text-status-amber",
      bg: "bg-status-amber-dim/30",
      border: "border-amber-500/50",
      label: "Cleared with Conditions",
    },
    not_clear: {
      icon: ShieldX,
      color: "text-status-red",
      bg: "bg-status-red-dim/30",
      border: "border-red-500/50",
      label: "Not Cleared",
    },
    pending_approval: {
      icon: ShieldAlert,
      color: "text-status-amber",
      bg: "bg-status-amber-dim/30",
      border: "border-amber-500/50",
      label: "Pending Approval",
    },
    deferred: {
      icon: ShieldAlert,
      color: "text-blue-400",
      bg: "bg-blue-900/30",
      border: "border-blue-500/50",
      label: "Deferred",
    },
    not_ready: {
      icon: ShieldAlert,
      color: "text-gray-400",
      bg: "bg-gray-900/30",
      border: "border-gray-500/50",
      label: "Not Ready",
    },
    // BM-H4498 (19 Oct 2026): the Pre-CaseMinder form saves its check as
    // pending_review. It had no entry here and fell through to "Not Ready",
    // which is a different state (a deferred check) and misled Vanessa.
    pending_review: {
      icon: ShieldAlert,
      color: "text-status-amber",
      bg: "bg-status-amber-dim/30",
      border: "border-amber-500/50",
      label: cc.registration_ref
        ? "Waiting for the steps done before CaseMinder"
        : "Pending review",
    },
  };

  // BM-H4498: an unknown status is named as it is, never shown as another state.
  const status = statusConfig[cc.conflict_status] || {
    icon: ShieldAlert,
    color: "text-gray-400",
    bg: "bg-gray-900/30",
    border: "border-gray-500/50",
    label: `Status: ${cc.conflict_status || "not recorded"}`,
  };
  const StatusIcon = status.icon;

  const hits = cc.hits_summary || {};
  const results =
    typeof hits === "object" && Array.isArray(hits.results) ? hits.results : [];
  const otherParties = cc.other_side_parties || [];

  return (
    <>
    <div className="min-h-screen bg-navy py-8 px-4" data-testid="conflict-check-detail">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* BM-H3368: where this matter is in the whole flow, and what's next. */}
        {(cc.matter_reference || cc.provisional_id) && (
          <NextStepBanner matterRef={cc.matter_reference || cc.provisional_id} />
        )}

        <div className="flex items-center justify-between">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-txt-muted hover:text-white hover:bg-navy-hover"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          <div className="flex gap-2">
            {/* BM-H613 (25 Sep 2026): a deferred check is completed by running
                the search for the now-known other side, on this same record
                and matter reference. */}
            {cc.conflict_status === "not_ready" && (
              <Button
                onClick={() => navigate(`/conflict-search?complete_deferred=${encodeURIComponent(cc.id || id)}`)}
                className="bg-status-amber hover:bg-status-amber text-black"
                data-testid="complete-deferred-check-btn"
              >
                <ShieldAlert className="w-4 h-4 mr-2" /> Complete deferred check
              </Button>
            )}
            {/* BM-H4498: a Pre-CaseMinder check is cleared by recording the
                historic conflict step, which also attaches the reference. */}
            {cc.conflict_status === "pending_review" && cc.registration_ref && (
              <Button
                onClick={() => navigate(`/matter/${encodeURIComponent(cc.registration_ref)}#pre-cm-history`)}
                className="bg-teal hover:bg-teal-hover text-white"
                data-testid="pre-cm-record-steps-from-conflict-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-2" /> Record steps done before CaseMinder
              </Button>
            )}
            {canSendOB && (
              <Button
                onClick={() => {
                  setObError(null);
                  setObResult(null);
                  setShowOBModal(true);
                }}
                className="bg-teal hover:bg-teal-hover text-white"
                data-testid="send-onboarding-btn"
              >
                <Send className="w-4 h-4 mr-2" /> Send Onboarding
              </Button>
            )}

            <Button
              onClick={() => window.open(`${API}/conflict-checks/${id}/download-pdf`, "_blank")}
              variant="outline"
              className="border-navy-border text-txt-muted hover:bg-navy-hover"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </div>
        </div>

        <div className={`${status.bg} border ${status.border} rounded-lg p-5 flex items-center gap-4`}>
          <StatusIcon className={`w-10 h-10 ${status.color}`} />
          <div>
            <h1 className="text-xl font-bold text-white">{clientDisplayName(cc)}</h1>
            <p className={`text-sm ${status.color} font-medium`}>{status.label}</p>
          </div>
        </div>

        {cc.onboarding_status && cc.onboarding_status !== "not_sent" && (
          <div className="bg-teal-dim/20 border border-teal/40 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-teal shrink-0" />
            <div>
              <p className="text-sm text-teal font-medium">Onboarding sent</p>
              <p className="text-xs text-txt-muted">Status: {cc.onboarding_status}</p>
            </div>
          </div>
        )}

        <div className="bg-navy border border-navy-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-navy-border">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal" /> Check Details
            </h2>
          </div>

          <div className="p-5 grid grid-cols-2 gap-4">
            <Detail label="Client Name" value={clientDisplayName(cc)} />
            <Detail label="Client Type" value={cc.client_type} />
            <Detail label="Decision Maker" value={cc.decision_maker_name} />
            <Detail label="Submitted By" value={cc.submitted_by} />
            <Detail label="Fee Earner" value={cc.fee_earner} />
            <Detail label="Matter Type" value={cc.matter_type} />
            <Detail
              label="Matter Reference"
              value={cc.matter_reference || cc.provisional_id
                || (cc.registration_ref && `${cc.registration_ref} (from the Pre-CaseMinder registration)`)}
              highlight
            />
            <Detail label="Search Query" value={cc.search_query} />
            <Detail
              label="Created"
              value={cc.created_at ? new Date(cc.created_at).toLocaleString("en-GB") : "-"}
            />
          </div>

          {cc.decision_notes && (
            <div className="mx-5 mb-5 bg-navy-hover border-l-3 border-navy-border p-3 rounded">
              <p className="text-[10px] uppercase tracking-wider text-txt-muted mb-1">
                Decision Notes
              </p>
              <p className="text-sm text-txt-muted">{cc.decision_notes}</p>
            </div>
          )}

          {cc.conditions && (
            <div className="mx-5 mb-5 bg-status-amber-dim/20 border-l-3 border-amber-500/50 p-3 rounded">
              <p className="text-[10px] uppercase tracking-wider text-status-amber mb-1">
                Conditions
              </p>
              <p className="text-sm text-txt-muted">{cc.conditions}</p>
            </div>
          )}
        </div>

        {otherParties.length > 0 && (
          <div className="bg-navy border border-navy-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-navy-border">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <User className="w-4 h-4 text-teal" /> Other Side Parties
              </h2>
            </div>

            <div className="p-4 space-y-2">
              {otherParties.map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-navy-hover rounded text-sm">
                  <span className="text-white font-medium">{p.legal_name || p.name || "Unknown"}</span>
                  <span className="text-txt-muted text-xs">{p.party_type}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {cc.auto_cleared_same_client_repeat && (
          <div
            className="bg-teal-dim/20 border-2 border-teal/50 rounded-lg p-4 flex items-center gap-3"
            data-testid="same-client-repeat-banner"
          >
            <div className="w-8 h-8 rounded-full bg-teal/20 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-teal" />
            </div>
            <div>
              <p className="text-base font-bold text-teal">No real conflicts — repeat client only</p>
              <p className="text-xs text-teal/70">
                {cc.auto_clear_reason ||
                  "Auto-cleared: the only match(es) were repeat instances of the same client. No supervisor approval was required."}
              </p>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className={`bg-navy border-2 rounded-lg overflow-hidden ${cc.auto_cleared_same_client_repeat ? "border-teal/50" : "border-amber-500/50"}`}>
            <div className={`p-4 border-b ${cc.auto_cleared_same_client_repeat ? "bg-teal-dim/20 border-teal/40" : "bg-status-amber-dim/20 border-amber-500/40"}`}>
              <h2 className={`text-sm font-semibold flex items-center gap-2 ${cc.auto_cleared_same_client_repeat ? "text-teal" : "text-status-amber"}`}>
                {cc.auto_cleared_same_client_repeat
                  ? <CheckCircle2 className="w-4 h-4" />
                  : <AlertTriangle className="w-4 h-4" />}
                {cc.auto_cleared_same_client_repeat ? "Repeat-Client Matches" : "Potential Matches"} ({results.length})
              </h2>
            </div>

            <div className="p-4 space-y-2">
              {results.map((match, i) => (
                <div
                  key={i}
                  className={`p-3 border rounded ${match.is_same_client_repeat ? "bg-teal-dim/20 border-teal/20" : "bg-status-amber-dim/30 border-amber-500/20"}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm text-white">
                      {match.name || match.client_name || "Unknown"}
                    </p>
                    {match.is_same_client_repeat && (
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal/20 text-teal border border-teal/30"
                        data-testid="repeat-client-tag"
                      >
                        Repeat client
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-txt-muted">
                    {match.matter_reference && (
                      <span>
                        Ref: <span className="font-mono">{match.matter_reference}</span>
                      </span>
                    )}
                    {match.matter_title && <span>{match.matter_title}</span>}
                    {match.entity_type && <span>Type: {match.entity_type}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BM-H3724: a pending check that never reached a director. */}
        <ConflictSendForApproval
          cc={cc}
          onSent={(r) => setCc((prev) => ({
            ...prev,
            approval_recipient_email: r?.recipient_email,
            approval_supervisor_email: r?.recipient_email,
          }))}
        />

        {(cc.approval_supervisor_email || cc.approval_email_status) && (
          <div className="bg-navy border border-navy-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-navy-border">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-teal" /> Approval Status
              </h2>
            </div>

            <div className="p-5 grid grid-cols-2 gap-4">
              <Detail label="Supervisor" value={cc.approval_supervisor_email} />
              <Detail label="Email Status" value={cc.approval_email_status} />
              <Detail label="Decision" value={cc.approval_decision} />
              <Detail label="Supervisor Notes" value={cc.approval_supervisor_reason} />
            </div>
          </div>
        )}
      </div>

      {showOBModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-navy border border-navy-border rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-navy-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-teal" />
                <h2 className="text-white font-semibold">Send Client Onboarding</h2>
              </div>

              <button
                onClick={() => {
                  setShowOBModal(false);
                  setObResult(null);
                  setObError(null);
                }}
                className="text-txt-muted hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {obResult ? (
              <div className="p-6 text-center space-y-3">
                <CheckCircle2 className="w-12 h-12 text-teal mx-auto" />
                <p className="text-white font-medium">Onboarding sent successfully</p>
                <p className="text-txt-muted text-sm">
                  Reference:{" "}
                  <span className="font-mono text-teal">
                    {obResult.provisional_id || "Created"}
                  </span>
                </p>
                {obResult.joint_client_count > 0 && (
                  <p className="text-txt-muted text-sm">
                    {obResult.joint_client_count} joint client(s) also invited
                  </p>
                )}
                <Button
                  onClick={() => {
                    setShowOBModal(false);
                    setObResult(null);
                  }}
                  className="bg-teal hover:bg-teal-hover text-white mt-2"
                >
                  Done
                </Button>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <div className="bg-navy-hover rounded-lg p-3">
                  <p className="text-xs text-txt-muted uppercase tracking-wider mb-1">Client</p>
                  <p className="text-white font-medium">{clientDisplayName(cc)}</p>
                </div>

                <div>
                  <label className="text-xs text-txt-muted uppercase tracking-wider block mb-1">
                    Client Email *
                  </label>
                  <input
                    type="email"
                    value={obForm.client_email}
                    onChange={(e) => setObForm((f) => ({ ...f, client_email: e.target.value }))}
                    placeholder="client@example.com"
                    className="w-full bg-navy-hover border border-navy-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal"
                  />
                </div>

                <div>
                  <label className="text-xs text-txt-muted uppercase tracking-wider block mb-1">
                    Fee Earner *
                  </label>
                  <input
                    type="text"
                    value={obForm.fee_earner}
                    onChange={(e) => setObForm((f) => ({ ...f, fee_earner: e.target.value }))}
                    placeholder="e.g. VEC"
                    className="w-full bg-navy-hover border border-navy-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal"
                  />
                </div>

                <div>
                  <label className="text-xs text-txt-muted uppercase tracking-wider block mb-1">
                    Fee Earner Email
                  </label>
                  <input
                    type="email"
                    value={obForm.fee_earner_email}
                    onChange={(e) => setObForm((f) => ({ ...f, fee_earner_email: e.target.value }))}
                    placeholder="fee.earner@bonsai.law"
                    className="w-full bg-navy-hover border border-navy-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal"
                  />
                </div>

                <div>
                  <label className="text-xs text-txt-muted uppercase tracking-wider block mb-1">
                    Matter Description
                  </label>
                  <input
                    type="text"
                    value={obForm.matter_description}
                    onChange={(e) => setObForm((f) => ({ ...f, matter_description: e.target.value }))}
                    placeholder="e.g. Residential Purchase"
                    className="w-full bg-navy-hover border border-navy-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal"
                  />
                </div>

                {obForm.joint_clients.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-txt-muted uppercase tracking-wider">Joint Clients</p>
                    {obForm.joint_clients.map((jc, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={jc.name}
                          onChange={(e) => updateJointClient(i, "name", e.target.value)}
                          placeholder="Full name"
                          className="flex-1 bg-navy-hover border border-navy-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal"
                        />
                        <input
                          type="email"
                          value={jc.email}
                          onChange={(e) => updateJointClient(i, "email", e.target.value)}
                          placeholder="Email"
                          className="flex-1 bg-navy-hover border border-navy-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal"
                        />
                        <button
                          type="button"
                          onClick={() => removeJointClient(i)}
                          className="text-status-red hover:text-status-red"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={addJointClient}
                  className="text-sm text-teal hover:text-teal flex items-center gap-1"
                >
                  <UserPlus className="w-4 h-4" /> Add joint client
                </button>

                {obError && (
                  <div className="bg-status-red-dim/30 border border-red-500/40 rounded-lg p-3">
                    <p className="text-status-red text-sm">{obError}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowOBModal(false);
                      setObError(null);
                    }}
                    className="flex-1 border-navy-border text-txt-muted"
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={handleSendOB}
                    disabled={obSending || !obForm.client_email.trim() || !obForm.fee_earner.trim()}
                    className="flex-1 bg-teal hover:bg-teal-hover text-white"
                  >
                    {obSending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" /> Send
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    {cc?.matter_reference && (
      <BonsaiMate context="matter" matterReference={cc.matter_reference} />
    )}
    </>
  );
}

function Detail({ label, value, highlight }) {
  const stringValue =
    value === null || value === undefined ? "" : typeof value === "string" ? value : String(value);

  const hasValue = stringValue.trim() !== "" && stringValue !== "-";

  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-txt-muted mb-0.5">{label}</p>
      <p
        className={`text-sm ${
          highlight && hasValue
            ? "text-teal font-mono"
            : hasValue
            ? "text-txt-muted"
            : "text-txt-faint italic"
        }`}
      >
        {hasValue ? stringValue : "Not specified"}
      </p>
    </div>
  );
}
